import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Week 2 · Genius One Plus (D2 + D5):
//   Trilingual only — English, Sinhala, Tamil.
//   Tamil is served to the Sri Lankan Tamil audience, so its flag is 🇱🇰
//   (not 🇮🇳). The 10 other languages that ship with the AI-Academy fork
//   remain in i18n.js as translation data but are HIDDEN from the picker.
// Week 3 add-on — 5 picker entries, with two BILINGUAL (native + English)
//   options interleaved after their native counterparts:
//     • `si-en-dual` for the English-aspiring Sinhalese middle class
//       (Colombo national schools, aspirational families) — 🇱🇰 flag.
//     • `ta-en-dual` for Chennai/Coimbatore + urban SL-Tamil parents —
//       🌐 flag (market-agnostic).
//   Both are powered by the `bilingual` postProcessor in i18n.js.
const LANGUAGES = [
  { code: 'en',          name: 'English',            flag: '🇬🇧' },
  { code: 'si',          name: 'සිංහල',              flag: '🇱🇰' },
  { code: 'si-en-dual',  name: 'සිංහල / English',   flag: '🇱🇰' },
  { code: 'ta',          name: 'தமிழ்',              flag: '🇱🇰' },
  { code: 'ta-en-dual',  name: 'தமிழ் / English',   flag: '🌐' },
  // Sprint 8 — regional expansion
  { code: 'hi',          name: 'हिन्दी',              flag: '🇮🇳' },
  { code: 'ms',          name: 'Bahasa Melayu',      flag: '🇲🇾' },
  { code: 'bn',          name: 'বাংলা',              flag: '🇧🇩' },
];

export default function LanguageSelector({ compact = false }) {
  const { i18n } = useTranslation();

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
  };

  // If the detector produces a code we're not offering (e.g. `zh` from the
  // browser locale), fall back to English so the trigger label is coherent.
  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={compact ? "sm" : "default"}
          className="gap-2"
          data-testid="language-selector-button"
        >
          <Globe className="h-4 w-4" />
          {!compact && <span>{currentLang.flag} {currentLang.name}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {LANGUAGES.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className="flex items-center gap-2 cursor-pointer"
            data-testid={`language-option-${lang.code}`}
          >
            <span className="text-xl">{lang.flag}</span>
            <span>{lang.name}</span>
            {i18n.language === lang.code && <Check className="ml-auto h-4 w-4 text-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
